import React, { useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import {
  FloatingPortal,
  safePolygon,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { MMMClientState } from "@north-beam/nb-common";

import { CLIENT_STATE_MAPPING_TO_INDEX } from "./top-bar";

const LAST_STEP_INDEX = 4;

const CLIENT_STATE_ACTION_REQUIRED_MAPPING: Record<MMMClientState, boolean> = {
  [MMMClientState.NOT_SET]: false,
  [MMMClientState.POPULATING_DATA]: true,
  [MMMClientState.REVIEWING_MODEL]: false,
  [MMMClientState.CREATING_SETTINGS]: true,
  [MMMClientState.PREPARING_MIX]: false,
  [MMMClientState.ADOPTING_MIX]: true,
  [MMMClientState.TRACKING_MIX]: false,
  [MMMClientState.REVIEWING_PERIOD]: false,
};

type Props = {
  children: React.ReactNode;
  cycleState: MMMClientState;
};

export const StepperStatePopover = ({ children, cycleState }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: "bottom",
  });
  const hover = useHover(context, { handleClose: safePolygon() });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <div>
      <div ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </div>
      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial={{ opacity: 0, translateY: "0.25rem" }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: "0.25rem" }}
              className="p-4 bg-v2-primary rounded-md border text-sm elevation-1 z-3"
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <div className="relative">
                <div className="top-[-23px] absolute-horizontal-center">
                  <div className="h-3 w-3 bg-v2-primary border-t border-l border-v2-secondary rotate-45"></div>
                </div>
              </div>
              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-2 -z-1"></div>
                <div className="grid grid-cols-[24px_1fr] gap-3 items-center">
                  <StepContent
                    text="Upload your data"
                    cycleState={cycleState}
                    index={0}
                  />
                  <StepContent
                    text="Create settings"
                    cycleState={cycleState}
                    index={1}
                  />
                  <StepContent
                    text="Preparing budgets"
                    cycleState={cycleState}
                    index={2}
                  />
                  <StepContent
                    text="Adopt a budget"
                    cycleState={cycleState}
                    index={3}
                  />
                  <StepContent
                    text="Setup complete"
                    cycleState={cycleState}
                    index={4}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
};

const StepToken = ({
  index,
  clientMappingIndex,
}: {
  index: number;
  clientMappingIndex: number;
}) => {
  const sharedClasses =
    "h-6 w-6 rounded-full flex-center z-1 text-xs font-semibold";
  const isCurrentStep = clientMappingIndex === index;

  if (clientMappingIndex > index) {
    return (
      <div className={`bg-good ${sharedClasses} text-white`}>
        <i className="fa-regular fa-check" />
      </div>
    );
  }

  const isLastStep = clientMappingIndex === LAST_STEP_INDEX;
  const isLastStepToken = index === LAST_STEP_INDEX;
  const number = index + 1;

  return (
    <div
      className={clsx(sharedClasses, {
        "bg-sun text-interstellar": isLastStep && isCurrentStep,
        "bg-primary text-white": !isLastStep && isCurrentStep,
        "bg-gray-2 text-gray+1": !isCurrentStep,
      })}
    >
      {isLastStepToken ? <i className="fa-solid fa-stars" /> : number}
    </div>
  );
};

const StepContent = ({
  index,
  text,
  cycleState,
}: {
  index: number;
  text: string;
  cycleState: MMMClientState;
}) => {
  const clientMappingIndex = CLIENT_STATE_MAPPING_TO_INDEX[cycleState];
  const clientActionRequired = CLIENT_STATE_ACTION_REQUIRED_MAPPING[cycleState];
  const isCurrentStep = clientMappingIndex === index;
  return (
    <>
      <StepToken index={index} clientMappingIndex={clientMappingIndex} />
      <div
        className={clsx("flex items-center gap-2 whitespace-nowrap", {
          "text-interstellar": isCurrentStep,
          "text-gray": !isCurrentStep,
        })}
      >
        {text}
        {clientActionRequired && isCurrentStep && (
          <div className="text-xxs text-mars bg-mars-2 rounded-md py-0.5 px-1.5 whitespace-nowrap">
            Action required
          </div>
        )}
      </div>
    </>
  );
};
